import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const TermsPopup = ({
  agreeChecked,
  setAgreeChecked,
  onAgree, // called only after API success
  userId, // ✅ recommended: pass from parent after signup
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorText, setErrorText] = useState("");
  const roboman_api = process.env.REACT_APP_ROBOMAN_API || "http://localhost:8881/api";

  const handleAgree = async () => {
    if (!agreeChecked || isSubmitting) return;

    setErrorText("");
    setIsSubmitting(true);

    try {
      // ✅ user_id is required by your curl payload
      const uid =
        userId ||
        localStorage.getItem("user_id") ||
        localStorage.getItem("request_user_id");

      if (!uid) {
        throw new Error("Missing user_id. Please signup/login again.");
      }

      // Optional auth (if your backend expects it)
      const token =
        localStorage.getItem("access_token")


      const res = await fetch(
        `${roboman_api}/users/terms-and-conditions/accept`, // ✅ include /api
        {
          method: "PUT",
          headers: {
            accept: "application/json",
            "Content-Type": "application/json"
            // ...(token ? { Authorization: `Bearer ${token}` } : {}), // optional
          },
          body: JSON.stringify({ user_id: uid }), // ✅ required payload
        }
      );

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        const msg =
          data?.detail ||
          data?.msg ||
          data?.message ||
          `Failed to accept terms (HTTP ${res.status}).`;
        throw new Error(msg);
      }

      if (typeof onAgree === "function") {
        onAgree(data);
      }
    } catch (err) {
      setErrorText(err?.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-md z-50 px-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          initial={{ scale: 0.85, opacity: 0, y: 30 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.85, opacity: 0, y: 30 }}
          transition={{ duration: 0.32, ease: "easeOut" }}
          className="
            w-full max-w-lg p-8 rounded-3xl relative
            bg-white/20 backdrop-blur-2xl border border-white/30
            shadow-[0_8px_50px_rgba(0,0,0,0.35)]
          "
        >
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/10 to-transparent pointer-events-none" />

          <h2 className="text-3xl font-extrabold text-center bg-gradient-to-r from-indigo-600 to-fuchsia-500 bg-clip-text text-transparent mb-4">
            Review & Accept
          </h2>

          <p className="text-white/90 text-center text-sm leading-relaxed mb-6">
            Before continuing, please confirm that you agree to our
            <br />
            <a
              href="/termsofservice"
              className="underline text-blue-200"
              target="_blank"
              rel="noreferrer"
            >
              Terms & Conditions
            </a>{" "}
            and{" "}
            <a
              href="/privacypolicy"
              className="underline text-blue-200"
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy
            </a>
            .
          </p>

          <div className="flex items-start gap-3 mb-4">
            <input
              type="checkbox"
              className="w-5 h-5 mt-1 accent-indigo-600"
              checked={agreeChecked}
              onChange={() => setAgreeChecked(!agreeChecked)}
              disabled={isSubmitting}
            />
            <label className="text-white/90 text-sm">
              I have read and agree to the Terms & Conditions and Privacy Policy.
            </label>
          </div>

          {errorText ? (
            <div className="mb-4 rounded-xl bg-red-500/20 border border-red-500/30 px-4 py-3 text-sm text-red-100">
              {errorText}
            </div>
          ) : null}

          <motion.button
            whileHover={agreeChecked && !isSubmitting ? { scale: 1.02 } : {}}
            whileTap={agreeChecked && !isSubmitting ? { scale: 0.98 } : {}}
            disabled={!agreeChecked || isSubmitting}
            onClick={handleAgree}
            className={`
              w-full py-3 rounded-2xl font-semibold text-white shadow-lg transition
              ${
                agreeChecked && !isSubmitting
                  ? "bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 hover:opacity-95"
                  : "bg-gray-400 cursor-not-allowed"
              }
            `}
          >
            {isSubmitting ? "Saving..." : "Agree & Continue"}
          </motion.button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TermsPopup;
